import { useCallback, useRef, useEffect } from 'react';

// These must match the constants in V3MapContainer
const CELL_SIZE = 60;
const MAP_EDGE_PADDING = 20;

/**
 * Hook that provides map centering on the player position.
 *
 * Handles both the centering function and the initial auto-scroll on first load.
 *
 * @param {Object} params
 * @param {React.RefObject} params.mapContainerRef - Ref to the scrollable map container
 * @param {Object|null} params.position - Player position { x, y }
 * @param {number} params.mapPaddingTop - Top padding of the map in pixels
 * @param {boolean} params.isLoading - Whether the app is still loading
 * @param {Array|null} params.map - Map data (truthy when map is loaded)
 * @param {string} params.activeTab - Currently active tab name
 * @returns {{ centerMapOnPlayer: Function }} - Function to center map, accepts instant boolean
 */
export function useMapCentering({ mapContainerRef, position, mapPaddingTop, isLoading, map, activeTab }) {
    const hasAutoScrolled = useRef(false);

    const centerMapOnPlayer = useCallback((instant = false) => {
        if (position && mapContainerRef.current) {
            const container = mapContainerRef.current;

            // First, let's find the actual player cell by marker
            const allCells = container.querySelectorAll('[data-x][data-y]');
            let actualPlayerPos = null;

            allCells.forEach(cell => {
                const chipLabel = cell.querySelector('div[style*="fontSize"] > div');
                if (chipLabel && chipLabel.textContent === '\uD83D\uDCCD') {
                    actualPlayerPos = {
                        x: parseInt(cell.getAttribute('data-x')),
                        y: parseInt(cell.getAttribute('data-y'))
                    };
                }
            });

            const playerPos = actualPlayerPos || position;

            // Validate position - check if it's the suspicious default 10,10
            if (playerPos.x === 10 && playerPos.y === 10 && !actualPlayerPos) {
                console.warn('Center: Suspicious default position 10,10 detected! This might be server coordinate 1,-1');
            }

            console.log('Center: Using player position:', playerPos, actualPlayerPos ? '(found by marker)' : '(from state)');

            const gap = 1;

            // Calculate exact player cell position (accounting for grid padding)
            const gridPaddingLeft = MAP_EDGE_PADDING;
            const gridPaddingTop = MAP_EDGE_PADDING + 0; // Extra space for region card
            const playerCellX = playerPos.x * (CELL_SIZE + gap) + gridPaddingLeft;
            const playerCellY = playerPos.y * (CELL_SIZE + gap) + mapPaddingTop + gridPaddingTop;

            // Calculate viewport center
            // For Y, we need to account for the region card taking up space at the top
            const viewportCenterX = container.clientWidth / 2;
            const visibleHeight = container.clientHeight - mapPaddingTop;
            const viewportCenterY = mapPaddingTop + (visibleHeight / 2);

            // Calculate scroll position to center the player cell
            const scrollLeft = playerCellX - viewportCenterX + (CELL_SIZE / 2);
            const scrollTop = playerCellY - viewportCenterY + (CELL_SIZE / 2);

            container.scrollTo({
                left: Math.max(0, scrollLeft),
                top: Math.max(0, scrollTop),
                behavior: instant ? 'instant' : 'smooth'
            });
        }
    }, [position, mapPaddingTop, mapContainerRef]);

    // Auto-center on player position when map loads (only if on world tab)
    useEffect(() => {
        if (!isLoading && map && position && mapContainerRef.current && !hasAutoScrolled.current && activeTab === 'world') {
            // Use requestAnimationFrame to ensure DOM is ready and measurements are accurate
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    // Use the centralized centering function with instant mode for cold start
                    centerMapOnPlayer(true);
                    hasAutoScrolled.current = true;
                });
            });
        }
    }, [isLoading, map, position, activeTab, centerMapOnPlayer, mapContainerRef]);

    return { centerMapOnPlayer };
}
